<script lang="ts" setup>
import type { ContentProduct } from '~/models/Content/ContentProduct'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { faArrowLeft, faLock, faUpRightFromSquare } from '@fortawesome/duotone-regular-svg-icons'
import arrayUtils from '~/utils/arrayUtils'
import useSubscriptionsStore from '~/stores/subscriptions'
import useLicenseControlStore from '~/stores/licenseControl'
import { useAuthStore } from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { useWebshopUrl } from '~/composables/useWebshopUrl.js'
import { useSubscription } from '~/composables/useSubscription'
import { useStaticProduct } from '~/composables/useStaticProduct'
import useProductHelper from '~/composables/useProductHelper'
import usePaywallOverlay from '~/composables/usePaywallOverlay'
import useContentApi from '~/api/contentApi'
import Button from '~/components/ui/Button.vue'
import VideoPlayer from '~/components/media/VideoPlayer.vue'

const { t } = useI18n()
const licenseControlStore = useLicenseControlStore()
const { isTeacher } = storeToRefs(useAuthStore())
const { resetLicenseControlStore } = licenseControlStore
const { hasAccessRestricted, locationPath } = storeToRefs(licenseControlStore)
const { washPage } = usePaywallOverlay()
const { isSubscriptionTrial, isSubscriptionBasic, isSubscriptionIndividual } = useSubscription()
const { relevantSubscription } = storeToRefs(useSubscriptionsStore())
const { findContents } = useContentApi()
const { truthy } = arrayUtils()
const { isExtraProduct, isLibrary, isExams, isPaperPlus } = useProductHelper()

const { Terminprover, Biblioteket, PapirPluss } = useStaticProduct()

// Not loaded by licenses, lookup needed
const pathLocationIds = computed(() =>
  (locationPath.value ?? '')
    .split('/')
    .map(id => Number(id))
    .filter(truthy)
    .sort((a, b) => a - b)
    .slice(3)
)

const { webshopUrl: fallbackWebshopUrl } = useWebshopUrl()
const { data: product } = useQuery<ContentProduct>({
  staleTime: Infinity,
  queryKey: computed(() => ['product-path', pathLocationIds.value]),
  enabled: computed(() => pathLocationIds.value.length > 0),
  queryFn: () => findContents<ContentProduct>({
    locationIdCriterion: pathLocationIds.value,
    contentTypeCriterion: [ContentType.Product],
  }, 1).then(([data]) => data)
})

const webshopUrl = computed(() => {
  if (!product?.value?.ean || !isExtraProduct(product.value)) {
    return fallbackWebshopUrl.value
  }

  const productUrls = {
    [Biblioteket.ean]: Biblioteket.aunivers.webshopUrl,
    [Terminprover.ean]: Terminprover.aunivers.webshopUrl,
    [PapirPluss.ean]: PapirPluss.aunivers.webshopUrl,
  }

  return productUrls[product.value?.ean] ?? fallbackWebshopUrl.value
})

const trialButtonText = computed(() => {
  if (!product?.value?.title)
    return t('licenseControl.buttons.trial')
  if (isExtraProduct(product.value))
    return product.value.title
  return t('licenseControl.buttons.trial')
})

const back = () => {
  resetLicenseControlStore()
  window.location.href = '/'
}

const upgradeMessage = computed((): string => {
  if (!product.value)
    return ''
  if (isPaperPlus(product.value) || isExams(product.value))
    return ''
  if (!relevantSubscription.value)
    return t('licenseControl.message.upgrade.noSubscription')
  if (isSubscriptionTrial(relevantSubscription.value))
    return t('licenseControl.message.upgrade.trial')
  if ((isSubscriptionBasic(relevantSubscription.value) || isSubscriptionIndividual(relevantSubscription.value))
    && isExtraProduct(product.value)
  ) {
    return t('licenseControl.message.upgrade.why')
  }
  return ''
})

const productMessage = computed((): string => {
  if (!product.value || !isExtraProduct(product.value))
    return ''
  if (isLibrary(product.value))
    return t('licenseControl.message.product.library')
  if (isExams(product.value))
    return t('licenseControl.message.product.exam')
  if (isPaperPlus(product.value))
    return t('licenseControl.message.product.paperPlus')
  return ''
})

const campaignVideo = computed(() => product.value && isExams(product.value) ? Terminprover.campaignVideo : undefined)
</script>

<template>
  <Transition
    v-if="hasAccessRestricted"
    appear
    class="paywall-overlay-ignore"
    enter-active-class="duration-500 delay-500"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    paywall-overlay-ignore
    tag="div"
    @after-enter="washPage"
  >
    <div class="absolute h-screen w-screen bg-gradient-to-b from-transparent via-white to-white">
      <div class="container mx-auto h-screen">
        <div class="border-gray-10 absolute inset-x-0 bottom-10 z-20 m-auto h-fit max-w-prose rounded-lg bg-white p-10 text-center sm:bottom-32 sm:border-2">
          <div class="bg-seagreen-10/50 text-seagreen-50 mb-3 inline-flex rounded-full p-2">
            <font-awesome-icon
              :icon="faLock"
              size="xl"
            />
          </div>
          <h3
            class="text-2xl font-bold"
            v-text="t('licenseControl.message.title')"
          />
          <p
            v-if="!isTeacher"
            class="mt-3 mb-5"
            v-text="t('licenseControl.message.body.student')"
          />
          <p
            v-if="isTeacher"
            class="mt-3 mb-5"
            v-text="upgradeMessage"
          />
          <p
            v-if="isTeacher"
            v-dompurify-html="productMessage"
            class="mt-3 mb-5"
          />
          <div
            v-if="isTeacher && campaignVideo"
            class="my-4"
          >
            <VideoPlayer
              :autoplay="false"
              :media-id="campaignVideo.contentItemId"
              :poster-src="campaignVideo.image"
              :src="campaignVideo.elementURI"
            />
          </div>
          <div
            :class="{ 'sm:grid-cols-2': isTeacher }"
            class="grid w-full grid-cols-1 justify-center gap-4"
          >
            <Button
              center
              full-width
              href="/"
              size="lg"
              @click.prevent="back"
            >
              <font-awesome-icon :icon="faArrowLeft" />
              {{ t('licenseControl.buttons.back') }}
            </Button>
            <Button
              v-if="isTeacher"
              :href="webshopUrl"
              center
              full-width
              shade="light"
              size="lg"
              target="_blank"
            >
              <font-awesome-icon :icon="faUpRightFromSquare" />
              {{ trialButtonText }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style>
.paywall-overlay-ignore {
  z-index: 300001 !important; /* more than Pendo guides which is 300000 */
}
</style>
